import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function WhatsAppProfile() {
  const { selectedBusiness } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ description: '', address: '', email: '', website: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [profilePic, setProfilePic] = useState(null);
  const [picPreview, setPicPreview] = useState(null);
  const [picMsg, setPicMsg] = useState('');
  const [picLoading, setPicLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    const bid = selectedBusiness?.id;
    if (!bid) return;
    api.get('/connect/profile', { params: { business_id: bid } }).then(r => {
      setProfile(r.data);
      setProfileForm({
        description: r.data.description || '',
        address: r.data.address || '',
        email: r.data.email || '',
        website: r.data.websites?.[0] || r.data.website || ''
      });
      if (r.data.profile_picture_url) setPicPreview(r.data.profile_picture_url);
    }).catch(() => {});
  }, [selectedBusiness]);

  const saveProfile = async () => {
    try {
      const bid = selectedBusiness?.id;
      await api.post('/connect/profile', { ...profileForm, business_id: bid });
      setProfileMsg('');
      setTimeout(() => setProfileMsg('✅ Profile saved!'), 50);
    } catch { setProfileMsg('❌ Failed to save'); }
  };

  const handlePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfilePic(file);
    setPicPreview(URL.createObjectURL(file));
    setPicMsg('');
  };

  const removePic = async () => {
    try {
      const bid = selectedBusiness?.id;
      await api.delete('/connect/profile-picture', { data: { business_id: bid } });
      setPicPreview(null);
      setProfilePic(null);
      setPicMsg('✅ Profile picture removed!');
    } catch { setPicMsg('❌ Failed to remove'); }
  };

  const uploadPic = async () => {
    if (!profilePic) return;
    setPicLoading(true);
    try {
      const bid = selectedBusiness?.id;
      const formData = new FormData();
      formData.append('photo', profilePic);
      formData.append('business_id', bid);
      await api.post('/connect/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPicMsg('✅ Profile picture updated!');
    } catch { setPicMsg('❌ Failed to upload'); }
    setPicLoading(false);
  };

  const S = {
    card: { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    label: { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 },
    inp: { width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 16, boxSizing: 'border-box' },
    btn: { width: '100%', padding: '11px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>📋 WhatsApp Profile</h2>

      {/* Profile Picture Section */}
      <div style={S.card}>
        <label style={S.label}>Profile Picture</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
          <div onClick={() => fileRef.current.click()} style={{ cursor: 'pointer', position: 'relative' }}>
            {picPreview ? (
              <img src={picPreview} alt="Profile" style={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: '3px solid #25D366' }} />
            ) : (
              <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#f3f4f6', border: '3px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>
                📷
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#25D366', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff' }}>✏️</div>
          </div>
          <div>
            <button onClick={() => fileRef.current.click()} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #25D366', background: '#fff', color: '#25D366', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>
              📁 Choose Photo
            </button>
            {profilePic && (
              <button onClick={uploadPic} disabled={picLoading} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#25D366', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {picLoading ? '⏳ Uploading...' : '⬆️ Upload'}
              </button>
            )}
            {picPreview && !profilePic && (
              <button onClick={removePic} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                🗑️ Remove
              </button>
            )}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePicChange} />
        {picMsg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: picMsg.includes('✅') ? '#f0fdf4' : '#fef2f2',
            color: picMsg.includes('✅') ? '#166534' : '#dc2626' }}>
            {picMsg}
          </div>
        )}
      </div>

      {/* Profile Info Section */}
      <div style={S.card}>
        <label style={S.label}>Display Name</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#f9fafb', flex: 1 }}>
            {profile?.verified_name || 'N/A'}
          </div>
          <span style={{
            padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: profile?.name_status === 'APPROVED' ? '#dcfce7' : profile?.name_status === 'DECLINED' ? '#fee2e2' : '#fef9c3',
            color: profile?.name_status === 'APPROVED' ? '#166534' : profile?.name_status === 'DECLINED' ? '#991b1b' : '#854d0e'
          }}>
            {profile?.name_status === 'APPROVED' ? '✅ Approved' : profile?.name_status === 'DECLINED' ? '❌ Rejected' : '⏳ In Review'}
          </span>
        </div>
        <label style={S.label}>Description</label>
        <div style={{ position: 'relative' }}>
          <textarea style={{ ...S.inp, height: 90, resize: 'vertical', paddingBottom: 20 }} placeholder="Business description..."
            maxLength={512} value={profileForm.description} onChange={e => setProfileForm({...profileForm, description: e.target.value})} />
          <div style={{ position: 'absolute', bottom: 6, right: 10, fontSize: 11, color: '#999' }}>{profileForm.description.length}/512</div>
        </div>
        <label style={S.label}>Address</label>
        <input style={S.inp} placeholder="Business address" value={profileForm.address} onChange={e => setProfileForm({...profileForm, address: e.target.value})} />
        <label style={S.label}>Email</label>
        <input style={S.inp} placeholder="Business email" value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} />
        <label style={S.label}>Website</label>
        <input style={S.inp} placeholder="https://www.example.com" value={profileForm.website} onChange={e => setProfileForm({...profileForm, website: e.target.value})} />
        <button onClick={saveProfile} style={{ ...S.btn, background: '#25D366', color: '#fff' }}>
          💾 Save Profile
        </button>
        {profileMsg && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: profileMsg.includes('✅') ? '#f0fdf4' : '#fef2f2',
            color: profileMsg.includes('✅') ? '#166534' : '#dc2626' }}>
            {profileMsg}
          </div>
        )}
      </div>
    </div>
  );
}
